import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Ban,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  Clock,
  ListTodo,
  Loader2,
  Mail,
  Plus,
  RefreshCcw,
  RotateCcw,
  Search,
  Trash2,
  UserX,
  X,
} from 'lucide-react';
import { pendientesAPI } from '../../api/api';

// ==================== Helpers de fecha ====================
// Importante: nunca usar toISOString() para obtener el día, porque convierte a
// UTC y en Madrid puede devolver el día anterior. Se construye a mano.
export const fechaISOLocal = (d) => {
  if (!d) return '';
  const anio = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${anio}-${mes}-${dia}`;
};

export const fechaEnDias = (dias) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + dias);
  return fechaISOLocal(d);
};

export const fechaManana = () => fechaEnDias(1);

// Próximo lunes (si hoy es lunes, el lunes de la semana que viene).
export const fechaLunesQueViene = () => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  const diasHastaLunes = ((8 - d.getDay()) % 7) || 7;
  d.setDate(d.getDate() + diasHastaLunes);
  return fechaISOLocal(d);
};

export const formatoFechaCorta = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
};

// Texto de edad del pendiente. Devuelve también si hay que pintarlo en rojo.
export const textoEdad = (p) => {
  if (!p) return { texto: '', alerta: false };
  if (p.fecha_limite && typeof p.dias_vencido === 'number') {
    const d = p.dias_vencido;
    if (d > 0) return { texto: d === 1 ? 'vencido hace 1 día' : `vencido hace ${d} días`, alerta: true };
    if (d === 0) return { texto: 'vence hoy', alerta: false };
    const faltan = Math.abs(d);
    return { texto: faltan === 1 ? 'vence en 1 día' : `vence en ${faltan} días`, alerta: false };
  }
  const abiertos = typeof p.dias_abierto === 'number' ? p.dias_abierto : 0;
  return { texto: abiertos === 1 ? 'lleva 1 día abierto' : `lleva ${abiertos} días abierto`, alerta: false };
};

// Hora de "Ha contestado": solo hh:mm si fue hoy; si no, con el día delante.
export const horaRespuesta = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hora = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString() ? hora : `${formatoFechaCorta(iso)} ${hora}`;
};

// Quién cerró (o descartó) un pendiente, según la última entrada del historial.
export const quienCerro = (p) => {
  const historial = Array.isArray(p?.historial) ? p.historial : [];
  for (let i = historial.length - 1; i >= 0; i -= 1) {
    const h = historial[i];
    if (h && (h.accion === 'hecho' || h.accion === 'descartado')) return h.usuario || '';
  }
  return '';
};

// Dominio de una dirección: "Marta Ruiz <marta@empresa.com>" -> "empresa.com".
export const dominioDe = (direccion) => {
  const limpio = String(direccion || '').trim().toLowerCase().replace(/^.*</, '').replace(/>.*$/, '');
  const partes = limpio.split('@');
  return partes.length > 1 ? (partes.pop() || '').trim() : '';
};

const DATOS_VACIOS = {
  resumen: {
    abiertos: 0, vencidos: 0, hoy: 0, esperando: 0, pospuestos: 0, hechos_hoy: 0, sugeridos: 0,
  },
  hoy: [],
  proximos: [],
  esperando: [],
  pospuestos: [],
  sugeridos: [],
  hechos: [],
};

const btnBase = 'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition';

// ==================== Modal de alta / conversión ====================
export const ModalPendiente = ({ abierto, onCerrar, onCreado, inicial }) => {
  const {
    titulo: tituloInicial = '',
    detalle: detalleInicial = '',
    email_id: emailIdInicial = null,
    presupuesto_id: presupuestoIdInicial = null,
    cliente_id: clienteIdInicial = null,
  } = inicial || {};

  const [titulo, setTitulo] = useState('');
  const [detalle, setDetalle] = useState('');
  const [fechaLimite, setFechaLimite] = useState(fechaEnDias(3));
  const [prioridad, setPrioridad] = useState('normal');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!abierto) return;
    setTitulo((tituloInicial || '').slice(0, 150));
    setDetalle(detalleInicial || '');
    setFechaLimite(fechaEnDias(3));
    setPrioridad('normal');
    setError(null);
  }, [abierto, tituloInicial, detalleInicial]);

  if (!abierto) return null;

  const guardar = async (e) => {
    e.preventDefault();
    if (!titulo.trim()) {
      setError('El título es obligatorio');
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const payload = {
        titulo: titulo.trim().slice(0, 150),
        detalle: detalle.trim() || null,
        fecha_limite: fechaLimite || null,
        prioridad,
      };
      if (emailIdInicial) payload.email_id = emailIdInicial;
      if (presupuestoIdInicial) payload.presupuesto_id = presupuestoIdInicial;
      if (clienteIdInicial) payload.cliente_id = clienteIdInicial;
      const { data } = await pendientesAPI.crear(payload);
      if (onCreado) onCreado(data);
      onCerrar();
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo crear el pendiente');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={guardar} className="bg-white rounded-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Añadir pendiente</h2>
          <button type="button" onClick={onCerrar} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <label className="block mb-3">
          <span className="text-xs font-medium text-gray-600">Título<span className="text-red-500">*</span></span>
          <input
            type="text"
            value={titulo}
            maxLength={150}
            onChange={(e) => setTitulo(e.target.value)}
            className="mt-1 w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            placeholder="Contestar a Laura con el presupuesto revisado"
            required
          />
        </label>

        <label className="block mb-3">
          <span className="text-xs font-medium text-gray-600">Detalle</span>
          <textarea
            value={detalle}
            rows={3}
            onChange={(e) => setDetalle(e.target.value)}
            className="mt-1 w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            placeholder="Contexto, importes, lo que haga falta recordar."
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Fecha límite</span>
            <input
              type="date"
              value={fechaLimite}
              onChange={(e) => setFechaLimite(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Prioridad</span>
            <select
              value={prioridad}
              onChange={(e) => setPrioridad(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            >
              <option value="normal">Normal</option>
              <option value="alta">Alta</option>
            </select>
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={guardando}
            className="px-4 py-2 text-sm bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary disabled:opacity-60 inline-flex items-center gap-2"
          >
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Guardar pendiente
          </button>
        </div>
      </form>
    </div>
  );
};

// ==================== Modal de reglas "No es mío" ====================
export const ModalReglas = ({ abierto, reglas, onCerrar, onQuitar }) => {
  if (!abierto) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Ban className="w-4 h-4 text-gray-500" /> Reglas: no es mío
          </h2>
          <button type="button" onClick={onCerrar} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Los correos de estos remitentes no crean pendientes, salvo que pidan algo a administración.
        </p>

        {(reglas || []).length === 0 ? (
          <div className="text-sm text-gray-500 italic py-4">Todavía no hay reglas.</div>
        ) : (
          <ul className="divide-y max-h-80 overflow-y-auto">
            {(reglas || []).map((r) => (
              <li key={r.id} className="py-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900 truncate">{r.valor}</div>
                  <div className="text-xs text-gray-500">
                    {r.tipo === 'dominio' ? 'Dominio entero' : 'Remitente'}
                    {r.created_at ? ` · desde el ${formatoFechaCorta(r.created_at)}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onQuitar(r)}
                  className={`${btnBase} bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200 shrink-0`}
                  title="Quitar la regla: sus correos vuelven a crear pendientes"
                >
                  <Trash2 className="w-3 h-3" /> Quitar
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex justify-end">
          <button type="button" onClick={onCerrar} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== Fila de un pendiente ====================
const FilaPendiente = ({
  pendiente,
  modo,
  menuAbierto,
  onAbrirMenu,
  onAccion,
  onAbrirCorreo,
}) => {
  const [confirmarHecho, setConfirmarHecho] = useState(false);
  const edad = textoEdad(pendiente);
  const contexto = pendiente.cliente_nombre || pendiente.remitente_nombre || pendiente.remitente || '';
  // Espera vencida que sale en "Hoy y vencidos": toca reclamar.
  const titulo = pendiente.reclamar
    ? `Reclamar a ${pendiente.remitente_nombre || pendiente.remitente || 'el cliente'}: ${pendiente.titulo}`
    : pendiente.titulo;
  const correosNuevos = pendiente.correos_nuevos || 0;
  const pulsarHecho = () => {
    // Si hay correos del hilo posteriores a nuestro último envío, se avisa antes:
    // "Hecho" no los oculta, siguen en IAmail.
    if (correosNuevos > 0 && !confirmarHecho) {
      setConfirmarHecho(true);
      return;
    }
    setConfirmarHecho(false);
    onAccion(pendiente, { estado: 'hecho' });
  };
  const esperandoOPospuesto = modo === 'esperando' || modo === 'pospuesto';
  // Sugerido: la IA cree que el correo era de otro compañero. No se cierra ni se
  // pospone desde aquí; solo se confirma si es tuyo o no.
  const esSugerido = modo === 'sugerido';

  const subtituloEspera = () => {
    if (pendiente.pospuesto_motivo === 'cobro') return 'Hasta que cobremos';
    const desde = pendiente.ultimo_correo_fecha || pendiente.updated_at || pendiente.created_at;
    const f = formatoFechaCorta(desde);
    return f ? `Esperando al cliente desde ${f}` : 'Esperando al cliente';
  };

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-semibold text-gray-900">{titulo}</span>
        {pendiente.respondido_en && !pendiente.reclamar && (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
            title={pendiente.respondido_por ? `Ha contestado ${pendiente.respondido_por}` : 'Ha contestado'}
          >
            Ha contestado {horaRespuesta(pendiente.respondido_en)}
          </span>
        )}
        {pendiente.reclamar && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
            Espera vencida
          </span>
        )}
        {pendiente.prioridad === 'alta' && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">Alta</span>
        )}
        {pendiente.n_correos > 1 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
            {pendiente.n_correos} correos
          </span>
        )}
      </div>

      <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-1.5">
        {contexto && <span className="truncate max-w-[220px]">{contexto}</span>}
        {contexto && pendiente.asunto_correo && <span>·</span>}
        {pendiente.asunto_correo && (
          <span className="truncate max-w-[280px]">{pendiente.asunto_correo}</span>
        )}
        {(contexto || pendiente.asunto_correo) && <span>·</span>}
        <span className={edad.alerta ? 'text-red-600 font-medium' : ''}>{edad.texto}</span>
      </div>

      {esSugerido && pendiente.sugerencia_motivo && (
        <div className="text-xs text-gray-500 italic mt-0.5">{pendiente.sugerencia_motivo}</div>
      )}

      {modo === 'esperando' && (
        <div className="text-xs text-amber-700 mt-0.5">{subtituloEspera()}</div>
      )}
      {modo === 'pospuesto' && pendiente.pospuesto_hasta && (
        <div className="text-xs text-gray-500 mt-0.5">
          Vuelve el {formatoFechaCorta(pendiente.pospuesto_hasta)}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {esperandoOPospuesto ? (
          <button
            type="button"
            onClick={() => onAccion(pendiente, { quitar_posponer: true })}
            className={`${btnBase} bg-duetto-primary/10 text-duetto-primary hover:bg-duetto-primary/20 border-duetto-primary/30`}
            title="Volver a ponerlo en la lista de hoy"
          >
            <RotateCcw className="w-3 h-3" /> Reactivar
          </button>
        ) : null}

        {esSugerido && (
          <button
            type="button"
            onClick={() => onAccion(pendiente, { es_mio: true })}
            className={`${btnBase} bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200`}
            title="Sí es tuyo: pasa a la lista normal de pendientes"
          >
            <CheckCircle2 className="w-3 h-3" /> Sí es mío
          </button>
        )}

        {!esSugerido && (
          <button
            type="button"
            onClick={pulsarHecho}
            className={`${btnBase} bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200`}
            title="Marcar como hecho"
          >
            <CheckCircle2 className="w-3 h-3" /> Hecho
          </button>
        )}

        {!esperandoOPospuesto && !esSugerido && (
          <button
            type="button"
            onClick={() => onAccion(pendiente, { posponer: { motivo: 'respuesta_cliente' } })}
            className={`${btnBase} bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200`}
            title="Queda a la espera de que conteste el cliente"
          >
            <Clock className="w-3 h-3" /> Esperando
          </button>
        )}

        {!esperandoOPospuesto && !esSugerido && (
          <div className="relative">
            <button
              type="button"
              onClick={() => onAbrirMenu(menuAbierto ? null : pendiente.id)}
              className={`${btnBase} bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200`}
              title="Posponer este pendiente"
            >
              <CalendarClock className="w-3 h-3" /> Posponer <ChevronDown className="w-3 h-3" />
            </button>
            {menuAbierto && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => onAbrirMenu(null)} />
                <div className="absolute left-0 top-full mt-1 z-20 w-60 bg-white border rounded-lg shadow-lg p-1 text-xs">
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50"
                    onClick={() => onAccion(pendiente, { posponer: { motivo: 'fecha', hasta: fechaManana() } })}
                  >
                    Mañana
                  </button>
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50"
                    onClick={() => onAccion(pendiente, { posponer: { motivo: 'fecha', hasta: fechaLunesQueViene() } })}
                  >
                    Lunes que viene
                  </button>
                  <div className="px-2 py-1.5 flex items-center gap-2">
                    <span className="text-gray-600 shrink-0">Elegir fecha</span>
                    <input
                      type="date"
                      className="flex-1 min-w-0 border rounded px-1 py-0.5 text-xs"
                      onChange={(e) => {
                        if (!e.target.value) return;
                        onAccion(pendiente, { posponer: { motivo: 'fecha', hasta: e.target.value } });
                      }}
                    />
                  </div>
                  {pendiente.presupuesto_id && (
                    <button
                      type="button"
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50"
                      onClick={() => onAccion(pendiente, { posponer: { motivo: 'despues_evento' } })}
                    >
                      Hasta después del evento
                    </button>
                  )}
                  {pendiente.presupuesto_id && (
                    <button
                      type="button"
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50"
                      onClick={() => onAccion(pendiente, { posponer: { motivo: 'cobro' } })}
                    >
                      Hasta que cobremos
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {pendiente.email_id && onAbrirCorreo && (
          <button
            type="button"
            onClick={() => onAbrirCorreo(pendiente.email_id)}
            className={`${btnBase} bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200`}
            title="Abrir el correo que originó este pendiente"
          >
            <Mail className="w-3 h-3" /> Abrir correo
          </button>
        )}

        {!esSugerido && (
          <button
            type="button"
            onClick={() => {
              if (!window.confirm(`¿Descartar el pendiente "${pendiente.titulo}"? Dejará de aparecer en la lista.`)) return;
              onAccion(pendiente, { estado: 'descartado' });
            }}
            className={`${btnBase} bg-gray-50 text-gray-500 hover:bg-gray-100 border-gray-200`}
            title="Descartar: ni se hace ni vuelve a aparecer"
          >
            <X className="w-3 h-3" /> Descartar
          </button>
        )}

        {esSugerido && (
          <button
            type="button"
            onClick={() => onAccion(pendiente, { es_mio: false })}
            className={`${btnBase} bg-gray-50 text-gray-500 hover:bg-gray-100 border-gray-200`}
            title="Quitarlo de mi lista: este correo no me da trabajo a mí"
          >
            <UserX className="w-3 h-3" /> No es mío
          </button>
        )}
      </div>

      {confirmarHecho && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1 min-w-[200px]">
            {correosNuevos === 1
              ? 'Hay 1 correo nuevo en el hilo. Marcar hecho no lo oculta; seguirá en IAmail.'
              : `Hay ${correosNuevos} correos nuevos en el hilo. Marcar hecho no los oculta; seguirán en IAmail.`}
          </span>
          <button
            type="button"
            onClick={pulsarHecho}
            className={`${btnBase} bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600`}
          >
            Continuar
          </button>
          <button
            type="button"
            onClick={() => setConfirmarHecho(false)}
            className={`${btnBase} bg-white text-gray-700 hover:bg-gray-50 border-gray-200`}
          >
            Cancelar
          </button>
        </div>
      )}
    </li>
  );
};

// ==================== Panel ====================
const PendientesPanel = ({ version = 0, onAbrirCorreo }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [verPospuestos, setVerPospuestos] = useState(false);
  const [verHechos, setVerHechos] = useState(false);
  const [ocultos, setOcultos] = useState(() => new Set());
  const [menuPosponer, setMenuPosponer] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [importando, setImportando] = useState(false);
  const [reglas, setReglas] = useState([]);
  const [modalReglasAbierto, setModalReglasAbierto] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = {};
      if (verHechos) params.incluir_hechos = true;
      const { data } = await pendientesAPI.listar(params);
      setDatos({ ...DATOS_VACIOS, ...(data || {}) });
      setOcultos(new Set());
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron cargar los pendientes');
    } finally {
      setCargando(false);
    }
  }, [verHechos]);

  const cargarReglas = useCallback(async () => {
    try {
      const { data } = await pendientesAPI.reglas();
      setReglas(Array.isArray(data) ? data : []);
    } catch (e) {
      setReglas([]);
    }
  }, []);

  // `version` sube en Emails.js cuando se gestiona, archiva o responde un
  // correo: cualquiera de esas acciones puede cambiar la lista de pendientes.
  useEffect(() => {
    cargar();
  }, [cargar, version]);

  useEffect(() => {
    cargarReglas();
  }, [cargarReglas]);

  // Actualización optimista: la fila desaparece al instante y se recarga
  // en segundo plano. Si el PATCH falla, se avisa y la recarga la devuelve.
  const accion = useCallback(async (pendiente, cambios) => {
    setMenuPosponer(null);
    setOcultos((prev) => {
      const next = new Set(prev);
      next.add(pendiente.id);
      return next;
    });
    try {
      await pendientesAPI.actualizar(pendiente.id, cambios);
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo actualizar el pendiente');
    } finally {
      cargar();
    }
  }, [cargar]);

  const quitarRegla = useCallback(async (regla) => {
    try {
      await pendientesAPI.eliminarRegla(regla.id);
      await cargarReglas();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo quitar la regla');
    }
  }, [cargarReglas]);

  const importar30Dias = useCallback(async () => {
    setImportando(true);
    try {
      await pendientesAPI.barrido({ dias: 30, dry_run: false });
      await cargar();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudieron importar los pendientes');
    } finally {
      setImportando(false);
    }
  }, [cargar]);

  const filtrar = useCallback((lista) => {
    const visibles = (lista || []).filter((p) => !ocultos.has(p.id));
    const q = busqueda.trim().toLowerCase();
    if (!q) return visibles;
    return visibles.filter((p) => (
      [p.titulo, p.cliente_nombre, p.remitente_nombre, p.remitente, p.asunto_correo]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    ));
  }, [busqueda, ocultos]);

  const resumen = datos?.resumen || DATOS_VACIOS.resumen;
  const listaHoy = filtrar(datos?.hoy);
  const listaProximos = filtrar(datos?.proximos);
  const listaEsperando = filtrar(datos?.esperando);
  const listaPospuestos = filtrar(datos?.pospuestos);
  const listaSugeridos = filtrar(datos?.sugeridos);
  const listaHechos = filtrar(datos?.hechos);

  const sinNada = Boolean(datos)
    && (resumen.abiertos || 0) === 0
    && (resumen.sugeridos || 0) === 0
    && (resumen.hechos_hoy || 0) === 0
    && (datos.hechos || []).length === 0;

  const renderLista = (lista, modo, vacio) => {
    if (!lista.length) {
      return <div className="text-sm text-gray-500 italic">{vacio}</div>;
    }
    return (
      <ul className="divide-y">
        {lista.map((p) => (
          <FilaPendiente
            key={p.id}
            pendiente={p}
            modo={modo}
            menuAbierto={menuPosponer === p.id}
            onAbrirMenu={setMenuPosponer}
            onAccion={accion}
            onAbrirCorreo={onAbrirCorreo}
          />
        ))}
      </ul>
    );
  };

  const chip = (etiqueta, valor, alerta = false, ambar = false) => {
    let tono = 'bg-gray-100 text-gray-700';
    if (alerta && valor > 0) tono = 'bg-red-50 text-red-700 border border-red-200';
    else if (ambar) tono = 'bg-amber-50 text-amber-800 border border-amber-200';
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full ${tono}`}>
        {etiqueta} <span className="font-semibold">{valor || 0}</span>
      </span>
    );
  };

  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <ListTodo className="w-4 h-4 text-duetto-primary" /> Pendientes
          </h2>
          {chip('Abiertos', resumen.abiertos)}
          {chip('Vencidos', resumen.vencidos, true)}
          {chip('Hoy', resumen.hoy)}
          {chip('Esperando', resumen.esperando)}
          {chip('Hechos hoy', resumen.hechos_hoy)}
          {(resumen.sugeridos || 0) > 0 && chip('Sugeridos', resumen.sugeridos, false, true)}
          {/* Solo si quedara alguna regla antigua: ya no se crean desde la UI. */}
          {reglas.length > 0 && (
            <button
              type="button"
              onClick={() => setModalReglasAbierto(true)}
              className="text-xs text-gray-500 hover:text-duetto-primary underline inline-flex items-center gap-1"
              title="Remitentes que no generan pendientes"
            >
              <Ban className="w-3 h-3" /> Reglas ({reglas.length})
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar"
              className="pl-7 pr-2 py-1.5 text-xs border rounded-lg w-40 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </div>
          <button
            type="button"
            onClick={() => setModalAbierto(true)}
            className={`${btnBase} bg-duetto-primary text-white hover:bg-duetto-secondary border-duetto-primary`}
          >
            <Plus className="w-3 h-3" /> Añadir pendiente
          </button>
          <button
            type="button"
            onClick={cargar}
            disabled={cargando}
            className={`${btnBase} bg-white text-duetto-primary hover:bg-duetto-primary/10 border-duetto-primary/40 disabled:opacity-50`}
          >
            <RefreshCcw className={`w-3 h-3 ${cargando ? 'animate-spin' : ''}`} /> Actualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {!datos && cargando && (
        <div className="text-sm text-gray-500 py-4 text-center">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Cargando pendientes…
        </div>
      )}

      {sinNada ? (
        <div className="text-sm text-gray-600 border rounded-lg p-4 bg-gray-50">
          <p className="mb-3">
            Todavía no hay pendientes. Puedes importar los de los últimos 30 días a partir de los
            correos ya analizados.
          </p>
          <button
            type="button"
            onClick={importar30Dias}
            disabled={importando}
            className={`${btnBase} bg-duetto-primary text-white hover:bg-duetto-secondary border-duetto-primary disabled:opacity-60`}
          >
            {importando ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Importar pendientes (30 días)
          </button>
        </div>
      ) : datos ? (
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase text-gray-500 font-semibold mb-1">Hoy y vencidos</div>
            {renderLista(listaHoy, 'hoy', 'Nada para hoy.')}
          </div>

          {listaSugeridos.length > 0 && (
            <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-3">
              <div className="text-xs uppercase text-amber-800 font-semibold">
                ¿Son tuyos? ({listaSugeridos.length})
              </div>
              <p className="text-xs text-amber-700 mb-1">
                La IA cree que estos correos son de otro compañero. Revisa y confirma.
              </p>
              {renderLista(listaSugeridos, 'sugerido', 'Nada que revisar.')}
            </div>
          )}

          {listaProximos.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs uppercase text-gray-500 font-semibold mb-1">Próximos</div>
              {renderLista(listaProximos, 'proximo', 'Nada próximo.')}
            </div>
          )}

          {listaEsperando.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs uppercase text-gray-500 font-semibold mb-1">Esperando respuesta</div>
              {renderLista(listaEsperando, 'esperando', 'Nada a la espera.')}
            </div>
          )}

          {(datos.pospuestos || []).length > 0 && (
            <div className="border-t pt-3">
              <button
                type="button"
                onClick={() => setVerPospuestos((v) => !v)}
                className="text-xs text-gray-600 hover:text-duetto-primary inline-flex items-center gap-1"
              >
                <ChevronDown className={`w-3 h-3 transition ${verPospuestos ? '' : '-rotate-90'}`} />
                {verPospuestos ? 'Ocultar pospuestos' : `Ver ${(datos.pospuestos || []).length} pospuestos`}
              </button>
              {verPospuestos && (
                <div className="mt-2">{renderLista(listaPospuestos, 'pospuesto', 'Ninguno pospuesto.')}</div>
              )}
            </div>
          )}

          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setVerHechos((v) => !v)}
              className="text-xs text-gray-600 hover:text-duetto-primary inline-flex items-center gap-1"
            >
              <ChevronDown className={`w-3 h-3 transition ${verHechos ? '' : '-rotate-90'}`} />
              {verHechos ? 'Ocultar hechos recientes' : 'Ver hechos recientes'}
            </button>
            {verHechos && (
              <ul className="mt-2 divide-y">
                {listaHechos.length === 0 && (
                  <li className="py-2 text-sm text-gray-500 italic">Sin pendientes cerrados todavía.</li>
                )}
                {listaHechos.map((p) => (
                  <li key={p.id} className="py-2 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-500 line-through truncate">{p.titulo}</div>
                      <div className="text-xs text-gray-400">
                        {p.estado === 'descartado' ? 'Descartado' : 'Hecho'}
                        {p.fecha_hecho ? ` el ${formatoFechaCorta(p.fecha_hecho)}` : ''}
                        {quienCerro(p) ? ` por ${quienCerro(p)}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => accion(p, { estado: 'pendiente' })}
                      className={`${btnBase} bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200 shrink-0`}
                    >
                      <RotateCcw className="w-3 h-3" /> Reabrir
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      <ModalPendiente
        abierto={modalAbierto}
        inicial={null}
        onCerrar={() => setModalAbierto(false)}
        onCreado={() => cargar()}
      />

      <ModalReglas
        abierto={modalReglasAbierto}
        reglas={reglas}
        onCerrar={() => setModalReglasAbierto(false)}
        onQuitar={quitarRegla}
      />
    </div>
  );
};

export default PendientesPanel;
